import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { EmojiPicker } from 'components/EmojiPicker/EmojiPicker';
import { useSendMessageMutation } from 'store/services/message/messageApi';
import { useSummonAdminMutation, useGetTradeCaseQuery } from 'store/services/case/caseApi';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import { AssetUrls } from 'config/assets';
import './ContactUserModal.css';

const MAX_IMAGES_PER_MESSAGE = 3;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  receiverId: number;
  receiverUsername: string;
  receiverAvatar?: string;
  tradeId?: string;
}

export const ContactUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  receiverId,
  receiverUsername,
  receiverAvatar,
  tradeId,
}) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hasJustSummoned, setHasJustSummoned] = useState(false);
  const [pendingImages, setPendingImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sendMessage, { isLoading }] = useSendMessageMutation();
  const [summonAdmin, { isLoading: isSummoning }] = useSummonAdminMutation();

  // Check if case exists for this trade (only if tradeId provided)
  const { data: caseData, refetch: refetchCase } = useGetTradeCaseQuery(tradeId!, {
    skip: !isOpen || !tradeId
  });

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setShowEmojiPicker(false);
      setHasJustSummoned(false);
      // Cleanup image preview URLs
      imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
      setPendingImages([]);
      setImagePreviewUrls([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newMessage = message.substring(0, start) + emoji + message.substring(end);

    setMessage(newMessage);

    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const addFiles = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const remainingSlots = MAX_IMAGES_PER_MESSAGE - pendingImages.length;
    if (remainingSlots <= 0) {
      toast.warning(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message`);
      return;
    }

    const filesToAdd: File[] = [];
    const newPreviewUrls: string[] = [];

    for (const file of files) {
      if (filesToAdd.length >= remainingSlots) {
        toast.warning(`Only ${remainingSlots} more image(s) can be added`);
        break;
      }

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`"${file.name}" is not supported. Only JPEG, PNG, and WEBP images are allowed.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`"${file.name}" is too large. Maximum file size is 5MB.`);
        continue;
      }

      filesToAdd.push(file);
      newPreviewUrls.push(URL.createObjectURL(file));
    }

    if (filesToAdd.length > 0) {
      setPendingImages(prev => [...prev, ...filesToAdd]);
      setImagePreviewUrls(prev => [...prev, ...newPreviewUrls]);
    }
  }, [pendingImages.length]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  }, [addFiles]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
      .filter((f): f is File => f !== null);

    if (imageFiles.length > 0) {
      e.preventDefault();
      addFiles(imageFiles);
    }
  }, [addFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, [addFiles]);

  const handleRemoveImage = useCallback((index: number) => {
    URL.revokeObjectURL(imagePreviewUrls[index]);
    setPendingImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  }, [imagePreviewUrls]);

  const handleSend = async () => {
    if ((!message.trim() && pendingImages.length === 0) || message.length > 2000) return;

    try {
      await sendMessage({
        receiverId,
        content: message.trim(),
        ...(pendingImages.length > 0 ? { images: pendingImages } : {}),
      }).unwrap();

      toast.success(`Message sent to ${receiverUsername}!`);
      onClose();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      // 429 errors are now handled globally in baseQuery
      // Only handle other errors here
      if (error.status !== 429) {
        toast.error(error.data?.message || 'Failed to send message. Please try again.');
      }
    }
  };

  const handleSummonAdmin = async () => {
    if (!tradeId) return;

    try {
      setHasJustSummoned(true);
      await summonAdmin({ tradeId }).unwrap();
      toast.success('Admin has been notified and will review your case');
      refetchCase();
    } catch (error: any) {
      setHasJustSummoned(false);
      console.error('Failed to summon admin:', error);
      toast.error(error.data?.message || 'Failed to summon admin. Please try again.');
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <div className="contact-modal-header">
          <h2 className="contact-modal-title">Contact {receiverUsername}</h2>
          <button className="contact-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="contact-modal-content">
          <div className="contact-user-info">
            <AvatarWithFlag
              avatarUrl={receiverAvatar}
              username={receiverUsername}
              size={40}
            />
            <span className="contact-username">{receiverUsername}</span>
          </div>

          <div
            className={`contact-form-group${isDragging ? ' drop-zone-active' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <label className="contact-label">Message</label>
            <textarea
              ref={textareaRef}
              className="contact-textarea"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onPaste={handlePaste}
              rows={6}
              maxLength={2000}
            />
            <CharacterCounter
              currentLength={message.length}
              maxLength={2000}
              warningThreshold={1800}
            />
          </div>

          {imagePreviewUrls.length > 0 && (
            <div className="image-preview-strip">
              {imagePreviewUrls.map((url, index) => (
                <div key={url} className="image-preview-item">
                  <img src={url} alt={`Preview ${index + 1}`} className="image-preview-thumbnail" />
                  <button
                    className="image-preview-remove"
                    onClick={() => handleRemoveImage(index)}
                    type="button"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="contact-actions">
            <div className="emoji-picker-container">
              <button
                ref={emojiButtonRef}
                className="emoji-picker-button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                😀
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                  buttonRef={emojiButtonRef}
                />
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <button
              className="image-attach-button"
              onClick={() => fileInputRef.current?.click()}
              type="button"
              title="Attach images"
              disabled={pendingImages.length >= MAX_IMAGES_PER_MESSAGE}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>

            {tradeId && !(caseData?.exists || hasJustSummoned) && (
              <MagicButton
                label="Summon Admin"
                color="black"
                onPress={handleSummonAdmin}
                isDisabled={isSummoning}
              />
            )}

            <MagicButton
              label={isLoading ? 'Sending...' : 'Send Message'}
              color="purple"
              onPress={handleSend}
              isDisabled={
                (!message.trim() && pendingImages.length === 0) ||
                message.length > 2000 ||
                isLoading
              }
            />
          </div>

          {tradeId && (caseData?.exists || hasJustSummoned) && (
            <div className="contact-summon-admin-section">
              <div className="case-summoned-status">
                <img
                  src={AssetUrls.icons.navigation.adminCases}
                  alt="Admin Summoned"
                  style={{ width: 20, height: 20 }}
                />
                <div>
                  <p className="summoned-text">Admin Summoned</p>
                  <p className="summoned-details">
                    {caseData?.summonedBy && `Case created by ${caseData.summonedBy}`}
                    {caseData?.alsoRequestedBy && ` and ${caseData.alsoRequestedBy}`}
                    {hasJustSummoned && !caseData?.exists && 'An admin will review shortly'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
